import { TicketLabel } from "shared/types/helpdesk";
// import { useNavigate, useLocation } from "react-router-dom";
import { Plus, CaretUp, CaretDown, LockKey } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { Accordion, AccordionItem } from "@heroui/react";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  UniversalTableRow,
  CellConfig,
  UniversalTableSearch,
} from "./components";
import type {
  UniversalFilterSection,
  UniversalGroupByOption,
} from "./components/UniversalTableSearch";
import {
  ColumnActionConfig,
  ColumnActionModalEvent,
} from "./types/columnActions";
import { useColumnActions } from "./hooks/useColumnActions";
import { useExcelExport } from "./hooks/useExcelExport";
import type {
  TableColumnsConfig,
  PickerConfig,
  TableSortConfig,
} from "./types";
import { useActiveTableContextOptional } from "./context/ActiveTableContext";
import { TABLE_DESIGN } from "./constants/tableDesign";
import {
  getTableContainerClass,
  getHeaderContainerClass,
  getColumnHeaderContainerClass,
  getColumnHeaderGridClass,
  getBadgeClass,
  getRowsContainerClass,
  getEmptyStateClass,
  getDragIndicatorClass,
  getResizeHandleClass,
  getResizeHandleLineClass,
} from "./utils/tableStyles";

/**
 * Props for UniversalTable component
 *
 * @template T - The row data type (must have optional `id` field)
 */
interface UniversalTableProps<T extends { id?: string | number; [key: string]: any }> {
  /** Array of data rows to display in the table */
  rows: T[];

  /** Title displayed at the top of the table */
  title: string;

  /** Optional description shown below the title */
  description?: string | null;

  /** Field to group rows by (e.g., 'building', 'status'). Use 'none' for no grouping. */
  groupBy?: string;

  /** ID of row to highlight (useful after navigation or creation) */
  highlightRowId?: number | null;

  /** Whether to show the "Quick Add" button */
  showQuickAdd?: boolean;

  /** Custom label for the quick add button (default: "Snel ticket toevoegen") */
  quickAddLabel?: string;

  /** Custom function to generate unique keys for rows (default uses row.id) */
  getRowKey?: (row: T, index: number) => string | number;

  /**
   * Search and filter configuration
   *
   * If provided, the table will render a search/filter UI bar below the table set sticky to the page bottom.
   * This supports text search, multi-select filters, grouping, and saved searches.
   */
  searchConfig?: {
    searchPlaceholder?: string;
    filterSections?: UniversalFilterSection[];
    groupByOptions?: UniversalGroupByOption[];
    onGroupByChange?: (groupBy: string) => void;
    savedSearches?: Array<{
      id: string;
      name: string;
      filters?: any;
      params?: string;
    }>;
    onSaveCurrentSearch?: (name?: string) => void;
    onApplySavedSearch?: (search: any) => void;
    syncWithURL?: boolean;
    urlParamPrefix?: string;
    onFiltersFromURL?: (filters: any) => void;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    activeFilters?: Record<string, string[]>;
    onFilterChange?: (key: string, values: string[]) => void;
    bulkActions?: any[];
    showSpacer?: boolean; // Show spacer div at bottom (default: true)
    /** If true, disables client-side filtering (use when filtering is done server-side) */
    serverSideFiltering?: boolean;
    /** Lazy loading state for showing loading indicator in search bar */
    isLoadingMore?: boolean;
    /** Custom content to show when lazy loading (e.g., Ella avatar) */
    lazyLoadingContent?: React.ReactNode;
    /** Optional: Provide a different dataset for extracting filter options */
    rowsForExtraction?: T[];
  };

  /**
   * Column action configurations
   *
   * Define domain-specific behaviors that should occur before/after field updates.
   * This keeps business logic in the parent component while leveraging table infrastructure.
   *
   * @see {@link ColumnActionConfig} for detailed documentation and examples
   */
  columnActions?: ColumnActionConfig[];

  /**
   * Handler called when a column action requests a modal
   *
   * Column actions can trigger custom modals via context.openModal().
   * The parent component should listen to this event and render the appropriate modal.
   *
   * @param event - Event containing modal configuration
   *
   * @example
   * ```typescript
   * const [modalConfig, setModalConfig] = useState<ModalConfig | null>(null);
   *
   * <UniversalTable
   *   onModalRequest={(event) => setModalConfig(event.config)}
   *   {...otherProps}
   * />
   *
   * {modalConfig?.type === 'supplier-suggestion' && (
   *   <SupplierSuggestionModal
   *     data={modalConfig.data}
   *     onConfirm={modalConfig.onConfirm}
   *     onCancel={() => setModalConfig(null)}
   *   />
   * )}
   * ```
   */
  onModalRequest?: (event: ColumnActionModalEvent) => void;

  // ========== Table Configuration (New Configuration-Based Architecture) ==========

  /**
   * Column configuration
   *
   * Defines table columns, their widths, sort keys, and visibility.
   * If not provided, falls back to legacy hardcoded columns (for backward compatibility).
   *
   * @example
   * ```typescript
   * columnsConfig={{
   *   columns: [
   *     { id: 'checkbox', label: <Checkbox />, minWidth: 40, defaultWeight: 0 },
   *     { id: 'title', label: 'Title', minWidth: 150, defaultWeight: 8, sortKey: 'title' }
   *   ],
   *   localStorageKey: 'my-table-column-widths'
   * }}
   * ```
   */
  columnsConfig?: TableColumnsConfig;

  /**
   * Set of column IDs to hide from the table.
   * Used with ColumnVisibilityToggle to let users show/hide columns.
   */
  hiddenColumns?: Set<string>;

  /**
   * Extra content rendered in the table header row, right-aligned.
   * Useful for column visibility toggles or other header actions.
   */
  headerExtra?: React.ReactNode;

  /**
   * Picker field configuration
   *
   * Defines how cell clicks map to picker modals and how updates are processed.
   * If not provided, falls back to legacy hardcoded picker logic (for backward compatibility).
   *
   * @example
   * ```typescript
   * pickerConfig={{
   *   entityName: 'ticket',
   *   entityPrefix: 'H',
   *   apiUpdateEndpoint: '/helpdesk/ticket/{id}',
   *   fieldMappings: [
   *     {
   *       eventMode: 'label',
   *       pickerMode: 'helpdesk-label',
   *       fieldName: 'label',
   *       extractCurrentValue: (row) => row.label,
   *       buildUpdatePayload: (val) => ({ label: val })
   *     }
   *   ]
   * }}
   * ```
   */
  pickerConfig?: PickerConfig;

  /**
   * Sort configuration
   *
   * Defines how rows should be sorted when clicking column headers.
   * If not provided, falls back to legacy hardcoded sort logic (for backward compatibility).
   *
   * @example
   * ```typescript
   * sortConfig={{
   *   defaultSortKey: 'date',
   *   defaultDirection: 'desc',
   *   fields: [
   *     { key: 'title', extractor: (row) => row.title.toLowerCase() },
   *     { key: 'date', extractor: (row) => new Date(row.createdAt).getTime() }
   *   ]
   * }}
   * ```
   */
  sortConfig?: TableSortConfig;

  /**
   * Custom row renderer
   *
   * If provided, this function is called for each row to generate cell content.
   * Return an array of CellConfig objects for custom cell rendering.
   */
  renderRow?: (
    row: T,
    context: {
      isSelected: boolean;
      isBulkActive: boolean;
      templateColumns: string;
      onToggleSelect: (selected: boolean) => void;
      onClearSelection: () => void;
    },
  ) => CellConfig[] | React.ReactNode;

  // ========== Legacy Event Handlers ==========
  // These are maintained for backward compatibility with existing implementations
  // For new implementations, consider using columnActions instead

  onComplete?: (rowId: number) => Promise<void>;
  onUpdate?: (rowId: number, updates: any) => void;
  onViewEmails?: (row: T, communicationMode?: "tenant" | "supplier") => void;
  onCreateInsurance?: (row: T) => void;
  onMerge?: (row: T) => void;
  onDelete?: (row: T) => void;
  onMoveToCompletion?: (rowId: number) => Promise<void>;
  onRevertToOpen?: (rowId: number) => Promise<void>;
  onSmartInsights?: (row: T) => void;
  onQuickAdd?: () => void;

  /** Optional handler for row clicks (e.g., open preview, navigate to detail) */
  onRowClick?: (row: T) => void;

  /** Unique identifier for this table instance (for state persistence) */
  tableId?: string;

  /**
   * Unique identifier for multi-table mode
   * When multiple tables exist on the same page, each needs a unique id for tracking
   */
  id?: string;

  /**
   * Enable multi-table mode
   * When true, the table registers with ActiveTableContext and doesn't render its own search bar
   * Instead, a shared SharedFilterBar adapts to whichever table is in view
   */
  multiTableMode?: boolean;

  // ========== Selection State ==========
  /** Externally controlled selection state (optional, table has internal state by default) */
  selectedRows?: Set<number>;

  /** Callback when selection changes (for controlled selection) */
  onSelectionChange?: (selectedRows: Set<number>) => void;

  // ========== Loading State ==========
  /** Whether the initial data is loading */
  isLoading?: boolean;
  /** Whether more rows are being loaded (for lazy loading) */
  isLoadingMore?: boolean;
  /** Custom loading state component to show when isLoading && rows.length === 0 */
  customLoadingState?: React.ReactNode;
  /** Custom empty state component to show when !isLoading && rows.length === 0 */
  customEmptyState?: React.ReactNode;
  lazyLoadTrigger?: React.ReactNode;

  // ========== Expandable Rows ==========
  /**
   * Set of row IDs that are currently expanded
   * Used with renderExpandedContent to show additional content below rows
   */
  expandedRows?: Set<number>;

  /**
   * Render function for expanded row content
   * Called for each row when its ID is in the expandedRows set
   * Returns the content to display below the row
   */
  renderExpandedContent?: (row: T) => React.ReactNode;

  // ========== Excel Export ==========
  /**
   * Excel export configuration
   *
   * If provided, enables Excel export functionality with a button in the search bar.
   * Supports exporting selected rows or all visible rows.
   */
  /**
   * Optional totals/summary row rendered below all data rows.
   *
   * Receives the currently sorted+filtered rows so the parent can compute
   * aggregates (sum, count, etc.). Returned cells use the same template
   * columns as the data rows so they line up with the headers.
   */
  footerCells?: (visibleRows: T[]) => CellConfig[];

  /**
   * Optional custom renderer for accordion group headers.
   *
   * When provided, replaces the default "key (N items)" title with the returned
   * ReactNode. The bulk-select checkbox is kept. When omitted, existing default
   * behaviour is preserved — backward-compatible with all existing consumers.
   *
   * @param groupKey - The raw groupBy field value for this group
   * @param groupRows - All rows belonging to this group (already filtered/sorted)
   */
  renderGroupHeader?: (
    groupKey: string,
    groupRows: T[],
    context?: { templateColumns: string },
  ) => React.ReactNode;

  /**
   * Optional second grouping level *within* each group (e.g. group by renovation
   * project, then by project part).
   *
   * When set, each group's body renders a nested accordion — one item per unique
   * value of this field — instead of a flat row list. All sub-groups start
   * expanded, so opening a group immediately shows its rows per sub-group.
   * Only applies when `groupBy` is not "none". Ignored when omitted, so existing
   * consumers keep their single-level grouping.
   *
   * Note: sub-groups follow the incoming row order (use a backend-ordered list),
   * while single-level groups keep their legacy "newest first" sort.
   */
  subGroupBy?: string;

  /**
   * Optional custom renderer for the sub-group accordion headers (see `subGroupBy`).
   * Falls back to "key (N items)" when omitted. The bulk-select checkbox is kept.
   */
  renderSubGroupHeader?: (
    subGroupKey: string,
    subGroupRows: T[],
    context?: { templateColumns: string },
  ) => React.ReactNode;

  /**
   * Allow several groups to be open at the same time (default: false, i.e. the
   * accordion's single-selection behaviour that existing tables rely on).
   */
  allowMultipleGroupsOpen?: boolean;

  excelExportConfig?: {
    /** Filename for the exported Excel file (without extension) */
    filename?: string;
    /** Sheet name within the Excel file */
    sheetName?: string;
    /** Whether to show export button (default: true if config provided) */
    enabled?: boolean;
    /** Custom button text (default: "Exporteer naar Excel") */
    buttonText?: string;
    /** Column IDs to exclude from export (e.g., ['checkbox', 'actions']) */
    excludeColumns?: string[];
    /** Column mapping: { columnId: dataFieldName } for mismatched IDs */
    columnMapping?: Record<string, string>;
    /** Number of columns to freeze from left */
    freezeColumns?: number;
    currencyColumns?: string[];
    /** Column IDs that should be formatted as currency */
    percentageColumns?: string[];
    /** Column IDs that should be formatted as percentage */
  };
}

/**
 * UniversalTable - A highly flexible, reusable table component
 *
 * This component provides a full-featured table with:
 * - Search and filtering
 * - Grouping and sorting
 * - Bulk selection and actions
 * - Column resizing
 * - Row actions
 * - Extensible column action system for domain-specific workflows
 *
 * @template T - The row data type
 *
 * @example Basic usage
 * ```typescript
 * <UniversalTable
 *   rows={tickets}
 *   title="Help Desk Tickets"
 *   description="All open tickets"
 *   onComplete={handleComplete}
 *   onUpdate={handleUpdate}
 *   {...otherHandlers}
 * />
 * ```
 *
 * @example With column actions
 * ```typescript
 * const columnActions: ColumnActionConfig[] = [
 *   {
 *     field: 'status',
 *     onAfterUpdate: async (newStatus, ticketIds, context) => {
 *       if (newStatus === 'closed') {
 *         await sendClosureNotifications(ticketIds);
 *         context.showToast('Notifications sent', 'success');
 *       }
 *     }
 *   }
 * ];
 *
 * <UniversalTable
 *   rows={tickets}
 *   columnActions={columnActions}
 *   onModalRequest={(event) => handleModalRequest(event)}
 *   {...otherProps}
 * />
 * ```
 */
export const UniversalTable = <T extends { id?: string | number; [key: string]: any }>({
  rows,
  title,
  description = null,
  groupBy: externalGroupBy,
  highlightRowId,
  showQuickAdd = false,
  quickAddLabel = "Snel ticket toevoegen",
  getRowKey = (row, index) => row.id ?? index,
  searchConfig,
  columnActions,
  onModalRequest,
  columnsConfig,
  hiddenColumns,
  headerExtra,
  pickerConfig,
  sortConfig,
  renderRow,
  onComplete,
  onUpdate,
  onViewEmails,
  onCreateInsurance,
  onMerge,
  onDelete,
  onMoveToCompletion,
  onRevertToOpen,
  onSmartInsights,
  onQuickAdd,
  onRowClick,
  tableId = "default",
  id,
  multiTableMode = false,
  selectedRows: externalSelectedRows,
  onSelectionChange,
  isLoading = false,
  customLoadingState,
  customEmptyState,
  lazyLoadTrigger,
  expandedRows,
  renderExpandedContent,
  footerCells,
  excelExportConfig,
  renderGroupHeader,
  subGroupBy,
  renderSubGroupHeader,
  allowMultipleGroupsOpen = false,
}: UniversalTableProps<T>) => {
  const { session } = useSession();
  const activeTableContext = useActiveTableContextOptional();

  // Initialize column actions hook
  const { executeAfterUpdateActions } = useColumnActions({
    columnActions,
    rows,
    session,
    onModalRequest,
  });

  // Initialize Excel export hook
  const { exportToExcel, isExporting } = useExcelExport();

  // Store searchConfig in a ref to avoid re-registration on every render
  const searchConfigRef = useRef(searchConfig);
  useEffect(() => {
    searchConfigRef.current = searchConfig;
  }, [searchConfig]);

  // Extract stable functions from context to avoid dependency on context object
  const registerTableFn = activeTableContext?.registerTable;
  const unregisterTableFn = activeTableContext?.unregisterTable;

  // Register table with ActiveTableContext if in multi-table mode
  useEffect(() => {
    if (
      multiTableMode &&
      registerTableFn &&
      unregisterTableFn &&
      id &&
      searchConfigRef.current
    ) {
      registerTableFn(id, {
        title,
        description: description || undefined,
        searchPlaceholder: searchConfigRef.current.searchPlaceholder,
        filterSections: searchConfigRef.current.filterSections || [],
        groupByOptions: searchConfigRef.current.groupByOptions || [],
        onSearchChange: searchConfigRef.current.onSearchChange || (() => {}),
        onFilterChange: searchConfigRef.current.onFilterChange || (() => {}),
        onGroupByChange: searchConfigRef.current.onGroupByChange || (() => {}),
      });

      return () => {
        if (id) {
          unregisterTableFn(id);
        }
      };
    }
  }, [
    multiTableMode,
    registerTableFn,
    unregisterTableFn,
    id,
    title,
    description,
  ]);

  // Internal filtering state (only used if searchConfig is provided without controlled state)
  const [internalSearchText, setInternalSearchText] = useState("");
  const [internalActiveFilters, setInternalActiveFilters] = useState<
    Record<string, string[]>
  >({});
  const [internalGroupBy, setInternalGroupBy] = useState<string>("none");

  // Use controlled state from searchConfig if provided, otherwise use internal state
  const searchText = searchConfig?.searchValue ?? internalSearchText;
  const setSearchText = searchConfig?.onSearchChange ?? setInternalSearchText;
  const activeFilters = searchConfig?.activeFilters ?? internalActiveFilters;
  const setActiveFilters = useCallback(
    (
      updater:
        | Record<string, string[]>
        | ((prev: Record<string, string[]>) => Record<string, string[]>),
    ) => {
      if (searchConfig?.onFilterChange) {
        // If controlled, we need to call onFilterChange for each changed filter
        const newFilters =
          typeof updater === "function" ? updater(activeFilters) : updater;
        Object.entries(newFilters).forEach(([key, values]) => {
          if (JSON.stringify(activeFilters[key]) !== JSON.stringify(values)) {
            searchConfig.onFilterChange!(key, values);
          }
        });
      } else {
        // If uncontrolled, use internal state
        setInternalActiveFilters(updater);
      }
    },
    [searchConfig, activeFilters],
  );

  // Use external or internal groupBy
  const groupBy = externalGroupBy ?? internalGroupBy;

  // navigation removed for row click
  const [internalSelectedRows, setInternalSelectedRows] = useState<Set<number>>(
    new Set(),
  );

  // Use external selection state if provided, otherwise use internal state
  const selectedRows = externalSelectedRows ?? internalSelectedRows;
  const setSelectedRows = useCallback(
    (newSelection: Set<number> | ((prev: Set<number>) => Set<number>)) => {
      if (onSelectionChange) {
        if (typeof newSelection === "function") {
          const currentSelection = externalSelectedRows ?? internalSelectedRows;
          onSelectionChange(newSelection(currentSelection));
        } else {
          onSelectionChange(newSelection);
        }
      } else {
        if (typeof newSelection === "function") {
          setInternalSelectedRows(newSelection);
        } else {
          setInternalSelectedRows(newSelection);
        }
      }
    },
    [onSelectionChange, externalSelectedRows, internalSelectedRows],
  );

  // Apply filtering to rows (only if searchConfig is provided and client-side filtering is enabled)
  const filteredRows = useMemo(() => {
    if (!searchConfig) return rows;

    // If server-side filtering is enabled, skip client-side filtering
    if (searchConfig.serverSideFiltering) return rows;

    const query = searchText.trim().toLowerCase();

    return rows.filter((row) => {
      // Search filter
      if (query) {
        // Build searchable string including ticket ID
        const searchableParts = [
          // Include ticket ID explicitly (for searching by H123, etc)
          row.id ? `H${row.id}` : "",
          row.id ? String(row.id) : "",
          // Include all other values
          ...Object.values(row).map((val) => {
            if (typeof val === "string") return val;
            if (typeof val === "object" && val !== null) {
              return Object.values(val).join(" ");
            }
            return "";
          }),
        ];

        const searchableString = searchableParts.join(" ").toLowerCase();

        if (!searchableString.includes(query)) return false;
      }

      // Apply each active filter
      for (const [filterKey, values] of Object.entries(activeFilters)) {
        if (!values || values.length === 0) continue;

        const rowValue = row[filterKey];
        const rowValueString =
          typeof rowValue === "object" && rowValue?.id
            ? String(rowValue.id) // Convert ID to string for comparison
            : String(rowValue || "");

        if (!values.includes(rowValueString)) return false;
      }

      return true;
    });
  }, [rows, searchText, activeFilters, searchConfig]);

  // Use filtered rows if search config is provided, otherwise use original rows
  const displayRows = searchConfig ? filteredRows : rows;

  const [isUpdating, setIsUpdating] = useState(false);
  const [pickerOpen, setPickerOpen] = useState<{
    open: boolean;
    mode:
      | "helpdesk-label"
      | "assignee"
      | "building"
      | "unit"
      | "supplier"
      | "reporter"
      | "source"
      | "custom-tags";
    eventMode?: string;
    title: string;
    targetIds: number[];
    currentValue?: any;
    currentBuildingId?: string;
    /**
     * Sleutel van de picker-config waarin `contextValue` moet belanden.
     * Zie `PickerFieldConfig.contextConfigKey`.
     */
    contextConfigKey?:
      | "currentBuildingId"
      | "currentUnitId"
      | "currentRenovationProjectId"
      | "contextValue";
    contextValue?: any;
    customOptions?:
      | string[]
      | {
          label: string;
          value: string;
          icon?: React.ReactNode;
          color?: string;
        }[];
    multiSelect?: boolean;
  }>({
    open: false,
    mode: "helpdesk-label",
    title: "",
    targetIds: [],
    currentValue: null,
  });

  // Sorting state (user-triggered sort via column headers)
  const [activeSortState, setActiveSortState] = useState<{
    key: string | null;
    direction: "asc" | "desc" | null;
  }>({ key: null, direction: null });

  // Ultra-fast checkbox handlers
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      const next = checked
        ? new Set(
            rows.map((r) => r.id).filter((id) => id !== undefined) as number[],
          )
        : new Set<number>();
      setSelectedRows(next);
    },
    [rows, setSelectedRows],
  );

  const handleToggleRowSelection = useCallback(
    (rowId: number, selected: boolean) => {
      setSelectedRows((prev: Set<number>) => {
        const next = new Set(prev);
        if (selected) next.add(rowId);
        else next.delete(rowId);
        return next;
      });
    },
    [setSelectedRows],
  );

  const handleToggleGroupSelection = useCallback(
    (groupRows: T[]) => {
      const groupRowIds = groupRows
        .map((r) => r.id)
        .filter((id) => id !== undefined) as number[];
      const allSelected = groupRowIds.every((id) => selectedRows.has(id));

      setSelectedRows((prev: Set<number>) => {
        const next = new Set(prev);
        if (allSelected) {
          // Deselect all rows in group
          groupRowIds.forEach((id) => next.delete(id));
        } else {
          // Select all rows in group
          groupRowIds.forEach((id) => next.add(id));
        }
        return next;
      });
    },
    [selectedRows, setSelectedRows],
  );

  // Sorting handler
  const handleSort = useCallback(
    (key: string) => {
      let direction: "asc" | "desc" = "asc";

      if (activeSortState.key === key && activeSortState.direction === "asc") {
        direction = "desc";
      }

      setActiveSortState({ key, direction });
    },
    [activeSortState],
  );

  // Debounce timer ref to prevent rapid modal opens
  const modalDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Listen to cell-triggered events to open the modal centrally
  useEffect(() => {
    const handler = (e: any) => {
      console.log(`[${tableId}] Opening modal`);
      const mode = e?.detail?.mode as
        | "label"
        | "assignee"
        | "building"
        | "unit"
        | "source"
        | "reporter"
        | "supplier"
        | "custom-tags";
      if (!mode) return;

      // Get the row ID from the event
      const rowId = e?.detail?.ticketId;

      // CRITICAL: Only handle events for rows that belong to this table
      const rowBelongsToThisTable = rows.some((r) => r.id === rowId);
      if (!rowBelongsToThisTable && selectedRows.size === 0) {
        console.log(
          `[${tableId}] Ignoring event for row ${rowId} - not in this table`,
        );
        return;
      }

      // Prevent duplicate modal if one is already open or we're updating
      if (pickerOpen.open || isUpdating) {
        console.log(`[${tableId}] Modal already open or updating, ignoring`);
        return;
      }

      // Clear any pending modal open
      if (modalDebounceRef.current) {
        clearTimeout(modalDebounceRef.current);
      }

      // Debounce the modal open to prevent rapid clicks
      modalDebounceRef.current = setTimeout(() => {
        console.log(`[${tableId}] Debounced modal open for row ${rowId}`);
        // Get the row to extract current values
        const row = rows.find((r) => r.id === rowId);

        // CRITICAL: If there are bulk selected rows, ALWAYS use bulk selection and IGNORE individual picker events
        // Unless the event explicitly says it's a bulk selection event
        const isBulkEvent = e?.detail?.bulkSelection === true;
        const ids =
          selectedRows.size > 0 || isBulkEvent
            ? [...selectedRows]
            : [rowId].filter(Boolean);

        // ========== NEW: Config-driven title generation ==========
        const fieldMapping = pickerConfig?.fieldMappings.find(
          (m) => m.eventMode === mode,
        );
        console.log("[UniversalTable] Event mode:", mode);
        console.log("[UniversalTable] PickerConfig:", pickerConfig);
        console.log("[UniversalTable] FieldMapping found:", fieldMapping);
        console.log(
          "[UniversalTable] CustomOptions:",
          fieldMapping?.customOptions,
        );

        let title: string;
        let pickerMode: string;
        let currentValue: any = null;
        let currentBuildingId: string | undefined = undefined;
        let contextValue: any = undefined;
        let contextConfigKey:
          | "currentBuildingId"
          | "currentUnitId"
          | "currentRenovationProjectId"
          | "contextValue"
          | undefined = undefined;

        if (fieldMapping) {
          // Use config to generate title
          pickerMode = fieldMapping.pickerMode;

          const count = ids.length;
          const entityName =
            count === 1
              ? pickerConfig!.entityName
              : pickerConfig!.entityPlural || `${pickerConfig!.entityName}s`;
          const entityPrefix = pickerConfig!.entityPrefix || "";
          const idsText = ids.map((id) => `${entityPrefix}${id}`).join(", ");
          const fieldName =
            pickerConfig!.fieldLabels?.[fieldMapping.fieldName] ||
            fieldMapping.fieldName;

          // Use custom title template if provided
          if (fieldMapping.titleTemplate) {
            title = fieldMapping.titleTemplate
              .replace("{count}", String(count))
              .replace("{entityName}", entityName)
              .replace("{entityPrefix}", entityPrefix)
              .replace("{ids}", idsText)
              .replace("{fieldName}", fieldName);
          } else {
            // Default template
            title = `Selecteer ${fieldName} voor ${count} ${entityName}`;
          }

          // Extract current value using config
          if (row && fieldMapping.extractCurrentValue) {
            currentValue = fieldMapping.extractCurrentValue(row);
          }

          // Extract context value (e.g., building ID for unit picker).
          // Niet elke picker filtert op gebouw: de onderdeel-picker verwacht
          // `currentRenovationProjectId`. De doelsleutel komt uit de config, met
          // een terugval per pickerMode zodat oudere configuraties blijven werken.
          if (row && fieldMapping.extractContextValue) {
            contextValue = fieldMapping.extractContextValue(row);
            contextConfigKey =
              fieldMapping.contextConfigKey ??
              (fieldMapping.pickerMode === "project-part"
                ? "currentRenovationProjectId"
                : "currentBuildingId");
            if (contextConfigKey === "currentBuildingId") {
              currentBuildingId = contextValue;
            }
          }

          // Het gebouw erbij, ook als de contextwaarde naar een andere sleutel
          // gaat. De onderdeel-picker gebruikt dit om, zolang er nog geen
          // renovatieproject gekozen is, meteen de projecten van dít gebouw te
          // tonen in plaats van een leeg zoekveld.
          if (row && fieldMapping.extractBuildingId && !currentBuildingId) {
            currentBuildingId = fieldMapping.extractBuildingId(row);
          }
        } else {
          // FALLBACK: Legacy hardcoded logic (backward compatibility)
          console.warn(
            `[UniversalTable] No picker config found for mode "${mode}". Using legacy title logic.`,
          );

          const countText =
            ids.length > 1
              ? `tickets H${ids.join(", H")}`
              : `ticket H${rowId ?? ""}`;
          const titleMap: Record<string, string> = {
            label: `Selecteer label voor ${countText}`,
            assignee: `Selecteer verantwoordelijke voor ${countText}`,
            building: `Selecteer gebouw voor ${countText}`,
            unit: `Selecteer unit voor ${countText}`,
            source: `Selecteer bron voor ${countText}`,
            reporter: `Selecteer melder voor ${countText}`,
            supplier: `Selecteer leverancier voor ${countText}`,
          };

          // Map old modes to new universal picker modes
          const modeMap: Record<string, any> = {
            label: "helpdesk-label",
            assignee: "assignee",
            building: "building",
            unit: "unit",
            source: "source",
            reporter: "reporter",
            supplier: "supplier",
            customTags: "custom-tags",
          };

          title = titleMap[mode];
          pickerMode = modeMap[mode] || "helpdesk-label";

          // Extract current value based on mode (legacy)
          if (row) {
            switch (mode) {
              case "label":
                currentValue = row.label;
                break;
              case "assignee":
                currentValue = row.assignedTo ? { id: row.assignedTo } : null;
                break;
              case "building":
                currentValue = row.building;
                currentBuildingId = row.building?.id || undefined;
                break;
              case "unit":
                currentValue = row.unit;
                currentBuildingId = row.building?.id || undefined;
                break;
              case "source":
                currentValue = row.source;
                break;
              case "reporter":
                currentValue = row.reporter;
                break;
              case "supplier":
                currentValue = row.supplier;
                break;
              case "custom-tags":
                currentValue = row.tags;
                break;
            }
          }
        }

        const pickerState = {
          open: true,
          mode: pickerMode as any,
          eventMode: mode,
          title,
          targetIds: ids,
          currentValue,
          currentBuildingId,
          contextConfigKey,
          contextValue,
          customOptions: fieldMapping?.customOptions,
          multiSelect: fieldMapping?.multiSelect,
        };
        console.log("[UniversalTable] Setting picker state:", pickerState);
        setPickerOpen(pickerState);
      }, TABLE_DESIGN.animations.modalDebounceMs);
    };
    window.addEventListener("helpdesk-open-picker", handler as any);
    return () => {
      window.removeEventListener("helpdesk-open-picker", handler as any);
      if (modalDebounceRef.current) {
        clearTimeout(modalDebounceRef.current);
      }
    };
  }, [selectedRows, rows, pickerOpen.open, isUpdating, tableId]);

  // ============ Column sizing (list view) ============
  // ========== NEW: Config-driven column definitions ==========
  // Get column definitions from config or use legacy hardcoded columns
  const effectiveColumns = useMemo(() => {
    if (columnsConfig?.columns) {
      if (hiddenColumns && hiddenColumns.size > 0) {
        return columnsConfig.columns.filter(col => !hiddenColumns.has(col.id));
      }
      return columnsConfig.columns;
    }

    // FALLBACK: Legacy hardcoded columns (backward compatibility)
    console.warn(
      "[UniversalTable] No columnsConfig provided. Using legacy hardcoded columns.",
    );
    return [
      {
        id: "checkbox",
        label: <></>, // Will be replaced with actual checkbox component
        minWidth: 40,
        defaultWeight: 0,
        className: "flex justify-center",
      },
      {
        id: "actions",
        label: "Acties",
        minWidth: 140,
        defaultWeight: 0,
        className: "flex justify-center",
      },
      {
        id: "comm",
        label: "Comm",
        minWidth: 80,
        defaultWeight: 0,
        className: "flex justify-center",
      },
      {
        id: "priority",
        label: "Prio",
        minWidth: 60,
        defaultWeight: 0,
        sortKey: "priority",
        className: "flex justify-center",
      },
      {
        id: "insurance",
        label: "Verz",
        minWidth: 50,
        defaultWeight: 0,
        className: "flex justify-center",
      },
      {
        id: "ticket-label",
        label: "Ticket & Label",
        minWidth: 150,
        defaultWeight: 8,
        sortKey: "label",
      },
      {
        id: "building",
        label: "Gebouw",
        minWidth: 80,
        defaultWeight: 3,
        sortKey: "building",
        visible: "lg" as const,
      },
      {
        id: "unit",
        label: "Unit",
        minWidth: 70,
        defaultWeight: 2,
        sortKey: "unit",
        visible: "lg" as const,
      },
      {
        id: "source",
        label: "Bron",
        minWidth: 18,
        defaultWeight: 0,
        sortKey: "source",
        visible: "xl" as const,
      },
      {
        id: "reporter",
        label: "Melder",
        minWidth: 100,
        defaultWeight: 2.5,
        sortKey: "reporter",
        visible: "lg" as const,
      },
      {
        id: "supplier",
        label: "Leverancier",
        minWidth: 100,
        defaultWeight: 2.5,
        sortKey: "supplier",
        visible: "lg" as const,
      },
      {
        id: "assignee",
        label: "Toegewezen",
        minWidth: 80,
        defaultWeight: 2,
        sortKey: "assignee",
      },
      {
        id: "date",
        label: "Datum",
        minWidth: 70,
        defaultWeight: 0,
        sortKey: "date",
        visible: "lg" as const,
      },
    ];
  }, [columnsConfig, hiddenColumns]);

  const TOTAL_TRACKS = effectiveColumns.length;
  const TRACK_MIN_WIDTHS = effectiveColumns.map((col) => col.minWidth);
  const defaultWidths = useRef<number[]>(
    effectiveColumns.map((col) => col.defaultWeight),
  );

  // Calculate sticky column offsets
  const stickyColumnOffsets = useMemo(() => {
    const offsets: (number | null)[] = [];
    let cumulativeOffset = 0;

    for (let i = 0; i < effectiveColumns.length; i++) {
      const col = effectiveColumns[i];
      if ((col as any).sticky) {
        offsets.push(cumulativeOffset);
        // Add this column's width to cumulative offset for next sticky column
        // Use minWidth as the base (will be replaced by actual width in styles)
        cumulativeOffset += col.minWidth;
      } else {
        offsets.push(null);
      }
    }

    return offsets;
  }, [effectiveColumns]);

  // ========== NEW: Config-driven localStorage key ==========
  const storageKey =
    columnsConfig?.localStorageKey !== null
      ? columnsConfig?.localStorageKey || `${tableId}-column-widths`
      : null;

  // Lazy initializer for column widths - only runs once on mount
  // This defers localStorage I/O until after initial paint for better performance
  const [columnWidths, setColumnWidths] = useState<number[]>(() => {
    if (!storageKey) return defaultWidths.current;

    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return defaultWidths.current;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length === TOTAL_TRACKS)
        return parsed as number[];
      return defaultWidths.current;
    } catch (error) {
      console.error("Failed to load column widths from localStorage:", error);
      return defaultWidths.current;
    }
  });
  const [hasInteracted, setHasInteracted] = useState<boolean>(false);
  const headerGridRef = useRef<HTMLDivElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const rowsContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const dragIndicatorRef = useRef<HTMLDivElement | null>(null);
  const tableHeaderRef = useRef<HTMLDivElement | null>(null);

  // Expose table header height as CSS variable for bulk actions bar
  useEffect(() => {
    const updateTableHeaderHeight = () => {
      const h = tableHeaderRef.current?.offsetHeight || 0;
      document.documentElement.style.setProperty(
        "--sticky-table-header-height",
        `${h}px`,
      );
    };

    updateTableHeaderHeight();
    let ro: ResizeObserver | null = null;
    try {
      // @ts-ignore
      ro = new ResizeObserver(() => updateTableHeaderHeight());
      if (tableHeaderRef.current) ro.observe(tableHeaderRef.current);
    } catch {}

    window.addEventListener("resize", updateTableHeaderHeight);
    return () => {
      window.removeEventListener("resize", updateTableHeaderHeight);
      if (ro && tableHeaderRef.current) ro.unobserve(tableHeaderRef.current);
    };
  }, [groupBy]);

  useEffect(() => {
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(columnWidths));
      } catch {}
    }
  }, [columnWidths, storageKey]);

  // Build template with fixed widths for UI columns and flexible for content columns
  const templateColumns = useMemo(() => {
    return columnWidths
      .map((w, i) => {
        // If weight is 0, use fixed width (min-width only)
        if (w === 0) {
          return `${TRACK_MIN_WIDTHS[i]}px`;
        }
        // Otherwise use flexible width with minimum
        return `minmax(${TRACK_MIN_WIDTHS[i]}px, ${Math.max(0.001, Math.round(w * 10000) / 10000)}fr)`;
      })
      .join(" ");
  }, [columnWidths]);

  // Helper to compute a template string without triggering React renders (for header-only preview)
  const getTemplateFromWeights = (weights: number[]) => {
    return weights
      .map((w, i) => {
        // If weight is 0, use fixed width (min-width only)
        if (w === 0) {
          return `${TRACK_MIN_WIDTHS[i]}px`;
        }
        // Otherwise use flexible width with minimum
        return `minmax(${TRACK_MIN_WIDTHS[i]}px, ${Math.max(0.001, Math.round(w * 10000) / 10000)}fr)`;
      })
      .join(" ");
  };

  // Reset column widths when the number of columns changes (e.g. dynamic columns added/removed)
  useEffect(() => {
    const newDefaults = effectiveColumns.map((col) => col.defaultWeight);
    defaultWidths.current = newDefaults;
    setColumnWidths(newDefaults);
    setHasInteracted(false);
  }, [TOTAL_TRACKS]);

  // Auto-fit initial widths to fill available space (full width) until user interacts
  useLayoutEffect(() => {
    if (hasInteracted) return;
    const el = headerGridRef.current;
    if (!el) return;
    // Initialize weights without forcing exact pixels
    setColumnWidths(defaultWidths.current);
  }, [hasInteracted]);

  useEffect(() => {
    if (hasInteracted) return;
    const onResize = () => {
      /* weights auto-fit by CSS fr; nothing to do */
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [columnWidths, hasInteracted]);

  // Drag to resize: adjust track i and i+1 while keeping their combined width constant
  const resizingRef = useRef<{
    index: number;
    startX: number;
    startWeights: number[];
    previewWeights: number[];
    headerRect: DOMRect; // Cache the header rect to avoid repeated getBoundingClientRect calls
  } | null>(null);
  const rafRef = useRef<number | null>(null);

  // Keep header horizontally in sync with the body scroll
  useEffect(() => {
    const scroller = scrollContainerRef.current;
    const headerEl = headerGridRef.current;
    if (!scroller || !headerEl || groupBy !== "none") return;

    const onScroll = () => {
      headerEl.style.transform = `translateX(${-scroller.scrollLeft}px)`;
    };
    // Initialize position
    onScroll();

    scroller.addEventListener("scroll", onScroll, { passive: true } as any);
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      headerEl.style.transform = "";
    };
  }, [groupBy]);

  const onMouseMove = (e: MouseEvent) => {
    const ctx = resizingRef.current;
    if (!ctx || !headerGridRef.current) return;

    const dx = e.clientX - ctx.startX;

    // Update drag indicator position using cached rect
    if (dragIndicatorRef.current && ctx.headerRect) {
      const x = e.clientX - ctx.headerRect.left;
      dragIndicatorRef.current.style.transform = `translateX(${x}px)`;
      dragIndicatorRef.current.style.display = "block";
    }

    // Calculate new weights using cached rect
    const i = ctx.index; // Column to the LEFT of the drag handle
    const j = i + 1; // Column to the RIGHT of the drag handle

    const totalPx = ctx.headerRect.width || 1;
    const totalWeight = ctx.startWeights.reduce((s, v) => s + v, 0);
    const pxPerWeight = totalPx / Math.max(totalWeight, 1);
    const weightDelta = dx / Math.max(pxPerWeight, 1);

    const newWeights = [...ctx.startWeights];
    // When dragging right (dx > 0): left column (i) GROWS, right column (j) shrinks
    // When dragging left (dx < 0): left column (i) SHRINKS, right column (j) grows
    // Calculate what the new pixel sizes would be
    const newLeftPx = (ctx.startWeights[i] + weightDelta) * pxPerWeight;
    const newRightPx = (ctx.startWeights[j] - weightDelta) * pxPerWeight;

    // Hard stop at minimum column width
    if (newLeftPx < TABLE_DESIGN.layout.minColumnWidthPx) {
      const minWeight = TABLE_DESIGN.layout.minColumnWidthPx / pxPerWeight;
      newWeights[i] = minWeight;
      newWeights[j] = ctx.startWeights[i] + ctx.startWeights[j] - minWeight;
    } else if (newRightPx < TABLE_DESIGN.layout.minColumnWidthPx) {
      const minWeight = TABLE_DESIGN.layout.minColumnWidthPx / pxPerWeight;
      newWeights[j] = minWeight;
      newWeights[i] = ctx.startWeights[i] + ctx.startWeights[j] - minWeight;
    } else {
      newWeights[i] = ctx.startWeights[i] + weightDelta;
      newWeights[j] = ctx.startWeights[j] - weightDelta;
    }

    ctx.previewWeights = newWeights;

    // Ultra-fast preview: only update header during drag, rows update on mouseup
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const template = getTemplateFromWeights(newWeights);
      if (headerGridRef.current) {
        headerGridRef.current.style.gridTemplateColumns = template;
      }
      // Rows will snap into place on mouseup for instant feedback
    });
  };

  const onMouseUp = () => {
    if (resizingRef.current && resizingRef.current.previewWeights) {
      // Apply the final weights - React will re-render all rows efficiently
      setColumnWidths(resizingRef.current.previewWeights);
    }

    // Hide drag indicator
    if (dragIndicatorRef.current) {
      dragIndicatorRef.current.style.display = "none";
    }

    // Clear header inline preview style (React state will take over)
    if (headerGridRef.current) {
      headerGridRef.current.style.gridTemplateColumns = "";
    }

    // Reset cursor
    document.body.style.cursor = "";
    document.body.style.userSelect = "";

    resizingRef.current = null;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  };

  const startResize = (trackIndex: number, ev: React.MouseEvent) => {
    // trackIndex is the left column of the pair to resize
    if (trackIndex < 0 || trackIndex >= TOTAL_TRACKS - 1) return;

    // Prevent resizing if either column is fixed width (weight = 0)
    const leftWeight = columnWidths[trackIndex];
    const rightWeight = columnWidths[trackIndex + 1];
    if (leftWeight === 0 || rightWeight === 0) return;

    if (!headerGridRef.current) return;

    ev.preventDefault();
    ev.stopPropagation();

    // Cache the header rect ONCE at the start of resize - huge performance boost!
    const headerRect = headerGridRef.current.getBoundingClientRect();

    resizingRef.current = {
      index: trackIndex,
      startX: ev.clientX,
      startWeights: [...columnWidths],
      previewWeights: [...columnWidths],
      headerRect: headerRect, // Cached rect avoids layout thrashing
    };
    setHasInteracted(true);

    // Show drag indicator at initial position
    if (dragIndicatorRef.current) {
      const x = ev.clientX - headerRect.left;
      dragIndicatorRef.current.style.transform = `translateX(${x}px)`;
      dragIndicatorRef.current.style.display = "block";
    }

    // Add col-resize cursor to body during drag
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const sortedRows = useMemo(() => {
    if (!displayRows || !Array.isArray(displayRows)) return [];
    const rowsCopy = [...displayRows];

    // ========== NEW: Config-driven sort logic ==========
    // If no sort active, apply default sort from config
    if (!activeSortState.key || !activeSortState.direction) {
      const defaultSortKey = sortConfig?.defaultSortKey;
      const defaultDirection = sortConfig?.defaultDirection || "desc";

      if (defaultSortKey && sortConfig?.fields) {
        const sortField = sortConfig.fields.find(
          (f) => f.key === defaultSortKey,
        );
        if (sortField) {
          return rowsCopy.sort((a: T, b: T) => {
            let comparison = 0;

            if (sortField.comparator) {
              comparison = sortField.comparator(a, b);
            } else if (sortField.extractor) {
              const aValue = sortField.extractor(a);
              const bValue = sortField.extractor(b);

              if (typeof aValue === "string" && typeof bValue === "string") {
                const caseSensitive = sortConfig?.caseSensitive || false;
                comparison = caseSensitive
                  ? aValue.localeCompare(bValue)
                  : aValue.toLowerCase().localeCompare(bValue.toLowerCase());
              } else if (
                typeof aValue === "number" &&
                typeof bValue === "number"
              ) {
                comparison = aValue - bValue;
              } else if (aValue instanceof Date && bValue instanceof Date) {
                comparison = aValue.getTime() - bValue.getTime();
              }
            }

            return defaultDirection === "asc" ? comparison : -comparison;
          });
        }
      }

      // FALLBACK: Legacy default sort (backward compatibility)
      return rowsCopy.sort((a: T, b: T) => {
        const dateA = new Date(a.createdAt || "").getTime();
        const dateB = new Date(b.createdAt || "").getTime();
        return dateB - dateA;
      });
    }

    // User-triggered sort (column header click)
    return rowsCopy.sort((a: T, b: T) => {
      // Try to find sort field config
      const sortField = sortConfig?.fields?.find(
        (f) => f.key === activeSortState.key,
      );

      if (sortField) {
        // ========== NEW: Use configured sort logic ==========
        let comparison = 0;

        if (sortField.comparator) {
          // Use custom comparator
          comparison = sortField.comparator(a, b);
        } else if (sortField.extractor) {
          // Use extractor
          const aValue = sortField.extractor(a);
          const bValue = sortField.extractor(b);

          if (typeof aValue === "string" && typeof bValue === "string") {
            const caseSensitive = sortConfig?.caseSensitive || false;
            comparison = caseSensitive
              ? aValue.localeCompare(bValue)
              : aValue.toLowerCase().localeCompare(bValue.toLowerCase());
          } else if (typeof aValue === "number" && typeof bValue === "number") {
            comparison = aValue - bValue;
          } else if (aValue instanceof Date && bValue instanceof Date) {
            comparison = aValue.getTime() - bValue.getTime();
          }
        }

        return activeSortState.direction === "asc" ? comparison : -comparison;
      }

      // FALLBACK: Legacy hardcoded sort logic (backward compatibility)
      console.warn(
        `[UniversalTable] No sort config found for key "${activeSortState.key}". Using legacy logic.`,
      );

      let aValue: any;
      let bValue: any;

      switch (activeSortState.key) {
        case "priority":
          const priorityOrder = { Hoog: 3, Gemiddeld: 2, Laag: 1, Unknown: 0 };
          aValue = priorityOrder[a.priority as keyof typeof priorityOrder] || 0;
          bValue = priorityOrder[b.priority as keyof typeof priorityOrder] || 0;
          break;
        case "building":
          aValue = a.building?.id || "";
          bValue = b.building?.id || "";
          break;
        case "unit":
          aValue = a.unit?.id || "";
          bValue = b.unit?.id || "";
          break;
        case "date":
          aValue = new Date(a.createdAt || "").getTime();
          bValue = new Date(b.createdAt || "").getTime();
          break;
        case "supplier":
          aValue = (a.supplier as any)?.name || "";
          bValue = (b.supplier as any)?.name || "";
          break;
        case "reporter":
          aValue = a.reporter
            ? `${(a.reporter as any).firstName || ""} ${(a.reporter as any).lastName || ""}`.trim()
            : "";
          bValue = b.reporter
            ? `${(b.reporter as any).firstName || ""} ${(b.reporter as any).lastName || ""}`.trim()
            : "";
          break;
        case "assignee":
          aValue = (a as any).assignedTo
            ? `${(a as any).assignedTo.first_name || ""} ${(a as any).assignedTo.last_name || ""}`.trim()
            : "";
          bValue = (b as any).assignedTo
            ? `${(b as any).assignedTo.first_name || ""} ${(b as any).assignedTo.last_name || ""}`.trim()
            : "";
          break;
        case "source":
          aValue = a.source || "";
          bValue = b.source || "";
          break;
        case "label":
          aValue = a.label || "";
          bValue = b.label || "";
          break;
        case "status":
          const statusOrder = {
            Ontvangen: 5,
            Geanalyseerd: 4,
            Gepland: 3,
            Afronding: 2,
            Gesloten: 1,
            Unknown: 0,
          };
          aValue =
            statusOrder[a.internalStatus as keyof typeof statusOrder] || 0;
          bValue =
            statusOrder[b.internalStatus as keyof typeof statusOrder] || 0;
          break;
        default:
          return 0;
      }

      // Handle string comparison
      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = aValue
          .toLowerCase()
          .localeCompare(bValue.toLowerCase());
        return activeSortState.direction === "asc" ? comparison : -comparison;
      }

      // Handle number comparison
      if (typeof aValue === "number" && typeof bValue === "number") {
        return activeSortState.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      // Fallback
      return 0;
    });
  }, [displayRows, activeSortState, sortConfig]);

  const groupedRows = useMemo(() => {
    if (groupBy === "none") return { "Alle Tickets": sortedRows };

    return sortedRows.reduce(
      (acc, row) => {
        const key: any =
          typeof row[groupBy as keyof T] === "object" &&
          (row[groupBy as keyof T] as any)?.id
            ? (row[groupBy as keyof T] as any).id
            : row[groupBy as keyof T] || "Unknown";

        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(row);
        return acc;
      },
      {} as Record<string, T[]>,
    );
  }, [sortedRows, groupBy]);

  /**
   * Splits a group's rows into sub-groups (see `subGroupBy`), preserving the
   * incoming row order. A Map is used on purpose: plain objects reorder
   * numeric-looking keys, which would scramble a backend-provided order.
   */
  const buildSubGroups = (groupRows: T[]): Array<[string, T[]]> => {
    const subGroups = new Map<string, T[]>();

    groupRows.forEach((row) => {
      const rawValue = row[subGroupBy as keyof T] as any;
      const key = String(
        rawValue && typeof rawValue === "object" && rawValue.id !== undefined
          ? rawValue.id
          : (rawValue ?? "Unknown"),
      );
      if (!subGroups.has(key)) subGroups.set(key, []);
      subGroups.get(key)!.push(row);
    });

    return Array.from(subGroups.entries());
  };

  /**
   * Renders a plain list of rows (no group header). Shared by the grouped and
   * the two-level grouped (`subGroupBy`) rendering so both stay identical.
   */
  const renderRowList = (rowsToRender: T[]) => (
    <div className={`divide-y divide-gray-100 ${TABLE_DESIGN.spacing.group}`}>
      {rowsToRender.map((row: T, index: number) => {
        const numericId = row.id as number;
        const rowContext = {
          isSelected: selectedRows.has(numericId),
          isBulkActive: selectedRows.size > 0,
          templateColumns,
          onToggleSelect: (selected: boolean) =>
            handleToggleRowSelection(numericId, selected),
          onClearSelection: () => setSelectedRows(new Set()),
        };

        const isExpanded =
          row.id !== undefined && expandedRows?.has(numericId);

        if (renderRow) {
          const result = renderRow(row, rowContext);
          if (Array.isArray(result)) {
            const visibleCells =
              hiddenColumns && hiddenColumns.size > 0
                ? result.filter((cell) => !hiddenColumns.has(cell.id))
                : result;
            return (
              <div key={getRowKey(row, index)}>
                <UniversalTableRow
                  ticket={row}
                  cells={visibleCells}
                  listTemplateColumns={templateColumns}
                  onClick={onRowClick ? () => onRowClick(row) : () => {}}
                />
                {isExpanded &&
                  renderExpandedContent &&
                  renderExpandedContent(row)}
              </div>
            );
          }
          return (
            <div key={getRowKey(row, index)}>
              {result}
              {isExpanded &&
                renderExpandedContent &&
                renderExpandedContent(row)}
            </div>
          );
        }

        return (
          <div key={getRowKey(row, index)}>
            <UniversalTableRow
              ticket={row}
              listTemplateColumns={templateColumns}
              isSelected={rowContext.isSelected}
              selectedTicketIds={undefined}
              isBulkActive={rowContext.isBulkActive}
              onToggleSelect={rowContext.onToggleSelect}
              onClearSelection={rowContext.onClearSelection}
              onClick={onRowClick ? () => onRowClick(row) : () => {}}
              onComplete={onComplete}
              onUpdate={onUpdate}
              onViewEmails={onViewEmails}
              onCreateInsurance={onCreateInsurance}
              onMerge={onMerge}
              onDelete={onDelete}
              onSmartInsights={onSmartInsights}
              onMoveToCompletion={onMoveToCompletion}
              onRevertToOpen={onRevertToOpen}
            />
            {isExpanded && renderExpandedContent && renderExpandedContent(row)}
          </div>
        );
      })}
    </div>
  );

  /** Group-level (and sub-group-level) bulk-select checkbox. */
  const renderGroupCheckbox = (groupRows: T[]) => {
    const groupRowIds = groupRows
      .map((r) => r.id)
      .filter((id) => id !== undefined) as number[];
    const allSelected =
      groupRowIds.length > 0 && groupRowIds.every((id) => selectedRows.has(id));
    const someSelected =
      groupRowIds.some((id) => selectedRows.has(id)) && !allSelected;

    return (
      <Checkbox
        checked={allSelected ? true : someSelected ? "indeterminate" : false}
        onCheckedChange={() => handleToggleGroupSelection(groupRows)}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        title={
          allSelected
            ? "Deselecteer alle items in deze groep"
            : "Selecteer alle items in deze groep"
        }
      />
    );
  };

  // Sortable header component
  const SortableHeader = ({
    children,
    sortKey,
    className = "",
  }: {
    children: React.ReactNode;
    sortKey?: string;
    className?: string;
  }) => {
    const isSorted = activeSortState.key === sortKey;
    const isAsc = isSorted && activeSortState.direction === "asc";

    if (!sortKey) {
      return <div className={`${className} flex items-center`}>{children}</div>;
    }

    return (
      <button
        onClick={() => handleSort(sortKey)}
        className={`${className} hover:text-moss-primary flex cursor-pointer items-center gap-1 transition-colors`}
        title={`Sorteer op ${sortKey}`}
      >
        <span className="truncate whitespace-nowrap">{children}</span>
        {isSorted &&
          (isAsc ? (
            <CaretUp className="text-moss-primary h-3 w-3 shrink-0" />
          ) : (
            <CaretDown className="text-moss-primary h-3 w-3 shrink-0" />
          ))}
      </button>
    );
  };

  return (
    <section ref={sectionRef as any} className={getTableContainerClass()}>
      {/* Search and Filters (if config provided) */}
      {searchConfig && (
        <div className={`${TABLE_DESIGN.colors.border}`}>
          <UniversalTableSearch
            rows={rows}
            rowsForExtraction={searchConfig.rowsForExtraction}
            searchValue={searchText}
            onSearchChange={setSearchText}
            searchPlaceholder={searchConfig.searchPlaceholder}
            filterSections={searchConfig.filterSections || []}
            activeFilters={activeFilters}
            onFilterChange={
              searchConfig.onFilterChange ||
              ((key, values) => {
                setActiveFilters((prev) => ({
                  ...prev,
                  [key]: values,
                }));
              })
            }
            groupByOptions={searchConfig.groupByOptions}
            selectedGroupBy={groupBy}
            onGroupByChange={(newGroupBy) => {
              if (searchConfig.onGroupByChange) {
                searchConfig.onGroupByChange(newGroupBy);
              } else {
                setInternalGroupBy(newGroupBy);
              }
            }}
            selectedCount={selectedRows.size}
            onClearSelection={() => setSelectedRows(new Set())}
            savedSearches={searchConfig.savedSearches}
            onSaveCurrentSearch={searchConfig.onSaveCurrentSearch}
            onApplySavedSearch={searchConfig.onApplySavedSearch}
            syncWithURL={searchConfig.syncWithURL}
            urlParamPrefix={searchConfig.urlParamPrefix}
            onFiltersFromURL={searchConfig.onFiltersFromURL}
            showSpacer={searchConfig.showSpacer}
            isLoadingMore={searchConfig.isLoadingMore}
            lazyLoadingContent={searchConfig.lazyLoadingContent}
            excelExportConfig={excelExportConfig}
            onExcelExport={(selectedRowsOnly) => {
              const rowsToExport = selectedRowsOnly
                ? Array.from(selectedRows)
                    .map((id) => rows.find((row) => row.id === id))
                    .filter(Boolean)
                : rows;
              exportToExcel(
                rowsToExport,
                effectiveColumns,
                Array.from(selectedRows)
                  .map((id) => rows.find((row) => row.id === id))
                  .filter(Boolean),
                {
                  filename:
                    excelExportConfig?.filename ||
                    title.toLowerCase().replace(/\s+/g, "-"),
                  sheetName: excelExportConfig?.sheetName || title,
                  selectedRowsOnly,
                  excludeColumns: excelExportConfig?.excludeColumns || [],
                  columnMapping: excelExportConfig?.columnMapping || {},
                  currencyColumns: excelExportConfig?.currencyColumns || [],
                  percentageColumns: excelExportConfig?.percentageColumns || [],
                  freezeColumns: excelExportConfig?.freezeColumns || 0,
                },
              );
            }}
            isExporting={isExporting}
          />
        </div>
      )}

      {/* Header */}
      <div className={getHeaderContainerClass()}>
        <div className="flex items-center gap-4">
          <h2 className={TABLE_DESIGN.typography.title}>
            {(() => {
              // Check if title contains count in parentheses (e.g., "Financials Gebouwen (20/39)")
              const countMatch = title.match(/^(.+?)\s+\((\d+\/\d+)\)$/);
              if (countMatch) {
                const [, mainTitle, count] = countMatch;
                return (
                  <>
                    {mainTitle}
                    <span className="text-neutral-secondary ml-3 font-normal">
                      ({count})
                    </span>
                  </>
                );
              }
              return title;
            })()}
          </h2>
          {title && (
            <span className={getBadgeClass()}>{displayRows.length}</span>
          )}
        </div>

        {/* Right side: header extra + bulk actions */}
        <div className="flex items-center gap-3">
          {headerExtra}
          {searchConfig?.bulkActions && (
            <div className="flex items-center gap-2">
            {searchConfig.bulkActions.map((action: any, index: number) => (
              <button
                key={index}
                onClick={action.onClick}
                disabled={action.disabled}
                className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                  backgroundColor:
                    action.variant === "red"
                      ? "#ef4444"
                      : action.variant === "blue"
                        ? "#3b82f6"
                        : action.variant === "yellow"
                          ? "#eab308"
                          : action.variant === "secondaryNeutral"
                            ? "#e5e7eb"
                            : "#059669",
                  color:
                    action.variant === "secondaryNeutral"
                      ? "#1f2937"
                      : "#ffffff",
                }}
              >
                {action.icon}
                {action.label}
              </button>
            ))}
            </div>
          )}
        </div>
      </div>

      {/* Sticky column headers (outside horizontal scroll, synced via transform) */}
      {(groupBy === "none" || renderGroupHeader) && (
        <div
          ref={tableHeaderRef}
          className={getColumnHeaderContainerClass()}
          style={{
            top: "var(--header-height, 70px)",
            willChange: "transform",
          }}
        >
          <div className="overflow-hidden">
            <div
              ref={headerGridRef}
              className={getColumnHeaderGridClass()}
              style={{
                gridTemplateColumns: templateColumns,
                minWidth: `${TABLE_DESIGN.responsive.minTableWidth}px`,
              }}
            >
              {(() => {
                // ========== NEW: Config-driven header segments ==========
                const nodes: JSX.Element[] = [];
                let trackCursor = 0;

                effectiveColumns.forEach((col, idx) => {
                  const span = (col as any).span || 1;
                  const start = trackCursor;
                  const end = start + span - 1;
                  trackCursor += span;
                  const atRightEdge = end >= TOTAL_TRACKS - 1;

                  // Build visibility classes from config
                  const visibilityClass =
                    col.visible === "lg"
                      ? TABLE_DESIGN.responsive.hiddenLgBlock
                      : col.visible === "xl"
                        ? TABLE_DESIGN.responsive.hiddenXlBlock
                        : "";

                  // Special handling for checkbox column
                  const displayLabel =
                    col.id === "checkbox" ? (
                      <Checkbox
                        checked={
                          selectedRows.size === sortedRows.length &&
                          sortedRows.length > 0
                        }
                        onCheckedChange={handleSelectAll}
                      />
                    ) : (col as any).isReadOnly ? (
                      // Read-only column with lock icon
                      <div className="flex items-center gap-2">
                        <span>{col.label}</span>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="cursor-help">
                                <LockKey
                                  size={14}
                                  weight="duotone"
                                  className="text-neutral-secondary-mid"
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                {(col as any).readOnlyTooltip ||
                                  "Alleen-lezen veld"}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    ) : (
                      col.label
                    );

                  // Sticky column classes and styles
                  const isSticky =
                    (col as any).sticky && stickyColumnOffsets[idx] !== null;
                  const stickyClasses = isSticky ? "sticky bg-white z-10" : "";
                  const stickyStyles = isSticky
                    ? { left: `${stickyColumnOffsets[idx]}px` }
                    : {};

                  nodes.push(
                    <div
                      key={`hdr-${col.id}`}
                      className={`${col.className || ""} ${visibilityClass} ${stickyClasses} relative col-span-${span} min-w-0 ${idx < effectiveColumns.length - 1 ? "border-neutral-primary/8 border-r" : ""}`}
                      style={stickyStyles}
                    >
                      <SortableHeader
                        sortKey={col.sortKey}
                        // De kop vult de kolom, dus het label zelf moet uitgelijnd
                        // worden: `justify-center` op de kolom-className raakt de
                        // volle-breedte knop eronder niet.
                        className={`w-full ${
                          (col as any).headerAlign === "center"
                            ? "justify-center"
                            : (col as any).headerAlign === "right"
                              ? "justify-end"
                              : ""
                        }`}
                      >
                        {displayLabel}
                      </SortableHeader>
                      {!atRightEdge &&
                        columnWidths[end] !== 0 &&
                        columnWidths[end + 1] !== 0 && (
                          <div
                            className={getResizeHandleClass()}
                            onMouseDown={(e) => startResize(end, e)}
                            title="Versleep om kolom te schalen"
                          >
                            <div className={getResizeHandleLineClass()} />
                          </div>
                        )}
                    </div>,
                  );
                });

                return nodes;
              })()}

              {/* Drag indicator - positioned with transform for smooth animation */}
              <div
                ref={dragIndicatorRef}
                className={getDragIndicatorClass()}
                style={{ left: 0, willChange: "transform" }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Table content with horizontal scroll */}
      <div
        className="bg-background mb-30 overflow-x-auto"
        ref={scrollContainerRef}
      >
        {groupBy === "none" ? (
          <div ref={rowsContainerRef} className={getRowsContainerClass()}>
            {/* Quick Add at top of list when there are few rows */}
            {showQuickAdd &&
              sortedRows.length <= TABLE_DESIGN.layout.quickAddThreshold &&
              onQuickAdd && (
                <div
                  className={`${TABLE_DESIGN.spacing.section} ${TABLE_DESIGN.colors.border} bg-moss-primary/5 border-b`}
                >
                  <div className="flex justify-center">
                    <ButtonNew
                      variant="primaryMoss"
                      onClick={onQuickAdd}
                      icon={<Plus />}
                      label={quickAddLabel}
                    />
                  </div>
                </div>
              )}

            {sortedRows.map((row, index) => {
              const numericId = row.id as number;
              const rowContext = {
                isSelected: selectedRows.has(numericId),
                isBulkActive: selectedRows.size > 0,
                templateColumns,
                onToggleSelect: (selected: boolean) =>
                  handleToggleRowSelection(numericId, selected),
                onClearSelection: () => setSelectedRows(new Set()),
                stickyColumnOffsets, // Add sticky column offsets to context
                effectiveColumns, // Add columns config for sticky check
              };

              const isExpanded = row.id !== undefined && expandedRows?.has(numericId);

              return (
                <div
                  key={getRowKey(row, index)}
                  id={`ticket-H${row.id}`}
                  className={`${row.id !== undefined && highlightRowId === row.id ? "ring-moss-primary animate-pulse rounded-md ring-2" : ""} ${selectedRows.has(numericId) ? "bg-moss-primary/5" : ""}`}
                >
                  {renderRow ? (
                    // Custom cell-based rendering
                    (() => {
                      const result = renderRow(row, rowContext);
                      if (Array.isArray(result)) {
                        // If renderRow returns CellConfig[], filter hidden columns and pass to UniversalTableRow
                        const visibleCells = hiddenColumns && hiddenColumns.size > 0
                          ? result.filter(cell => !hiddenColumns.has(cell.id))
                          : result;
                        return (
                          <UniversalTableRow
                            ticket={row}
                            cells={visibleCells}
                            listTemplateColumns={templateColumns}
                            onClick={
                              onRowClick ? () => onRowClick(row) : () => {}
                            }
                          />
                        );
                      } else {
                        // If renderRow returns ReactNode, render it directly
                        return result;
                      }
                    })()
                  ) : (
                    // Legacy HelpdeskTicket component rendering
                    <UniversalTableRow
                      ticket={row}
                      listTemplateColumns={templateColumns}
                      isSelected={rowContext.isSelected}
                      selectedTicketIds={undefined}
                      isBulkActive={rowContext.isBulkActive}
                      onToggleSelect={rowContext.onToggleSelect}
                      onClearSelection={rowContext.onClearSelection}
                      onClick={onRowClick ? () => onRowClick(row) : () => {}}
                      onComplete={onComplete}
                      onUpdate={onUpdate}
                      onViewEmails={onViewEmails}
                      onCreateInsurance={onCreateInsurance}
                      onMerge={onMerge}
                      onDelete={onDelete}
                      onSmartInsights={onSmartInsights}
                      onMoveToCompletion={onMoveToCompletion}
                      onRevertToOpen={onRevertToOpen}
                    />
                  )}
                  {/* Expanded content below row */}
                  {isExpanded && renderExpandedContent && renderExpandedContent(row)}
                </div>
              );
            })}

            {/* Footer / totals row (rendered after all data rows when provided) */}
            {footerCells && sortedRows.length > 0 && (() => {
              try {
                const cells = footerCells(sortedRows);
                if (!cells || cells.length === 0) return null;
                const visibleCells =
                  hiddenColumns && hiddenColumns.size > 0
                    ? cells.filter((cell) => !hiddenColumns.has(cell.id))
                    : cells;
                return (
                  <div
                    data-universal-footer-row
                    className="border-t-2 border-neutral-200 bg-neutral-50 font-semibold"
                  >
                    <UniversalTableRow
                      ticket={{}}
                      cells={visibleCells}
                      listTemplateColumns={templateColumns}
                      onClick={() => {}}
                    />
                  </div>
                );
              } catch (error) {
                console.error(
                  "[UniversalTable] Error rendering footerCells:",
                  error,
                );
                return null;
              }
            })()}

            {sortedRows.length === 0 && (
              <div className={getEmptyStateClass()}>
                {isLoading ? (
                  // Show loading state when data is being fetched
                  customLoadingState || (
                    <>
                      <div className="mb-4 text-4xl">⏳</div>
                      <p className={TABLE_DESIGN.typography.emptyTitle}>
                        Gegevens laden...
                      </p>
                      <p className={TABLE_DESIGN.typography.emptyDescription}>
                        Een moment geduld
                      </p>
                    </>
                  )
                ) : (
                  // Show empty state when no data found
                  customEmptyState || (
                    <>
                      <div className="mb-4 text-4xl">📋</div>
                      <p className={TABLE_DESIGN.typography.emptyTitle}>
                        Geen data gevonden
                      </p>
                      <p className={TABLE_DESIGN.typography.emptyDescription}>
                        {title.includes("Afronding")
                          ? "Er zijn momenteel geen data in de afrondingsfase"
                          : "Probeer je filters aan te passen of maak een nieuw item aan"}
                      </p>
                      {showQuickAdd && onQuickAdd && (
                        <div className="flex justify-center">
                          <ButtonNew
                            variant="primaryMoss"
                            onClick={onQuickAdd}
                            icon={<Plus />}
                            label={quickAddLabel}
                          />
                        </div>
                      )}
                    </>
                  )
                )}
              </div>
            )}
          </div>
        ) : (
          <div className={TABLE_DESIGN.spacing.section}>
            <Accordion
              selectionMode={allowMultipleGroupsOpen ? "multiple" : "single"}
            >
              {Object.entries(groupedRows).map(([group, groupRows]) => {
                const subGroups = subGroupBy ? buildSubGroups(groupRows) : null;

                return (
                  <AccordionItem
                    key={group}
                    title={
                      <div className="flex items-center gap-2">
                        {renderGroupCheckbox(groupRows)}
                        {renderGroupHeader
                          ? renderGroupHeader(group, groupRows, { templateColumns })
                          : <span>{`${group} (${groupRows.length} items)`}</span>}
                      </div>
                    }
                  >
                    {subGroups ? (
                      // Tweede groeperingsniveau. Alle sub-groepen staan open,
                      // zodat het openklikken van een groep meteen alle rijen
                      // per sub-groep toont.
                      <Accordion
                        selectionMode="multiple"
                        defaultExpandedKeys={subGroups.map(([subKey]) => subKey)}
                      >
                        {subGroups.map(([subKey, subRows]) => (
                          <AccordionItem
                            key={subKey}
                            title={
                              <div className="flex items-center gap-2">
                                {renderGroupCheckbox(subRows)}
                                {renderSubGroupHeader
                                  ? renderSubGroupHeader(subKey, subRows, { templateColumns })
                                  : <span>{`${subKey} (${subRows.length} items)`}</span>}
                              </div>
                            }
                          >
                            {renderRowList(subRows)}
                          </AccordionItem>
                        ))}
                      </Accordion>
                    ) : (
                      renderRowList(
                        [...groupRows].sort(
                          (a: any, b: any) =>
                            new Date(b.createdAt).getTime() -
                            new Date(a.createdAt).getTime(),
                        ),
                      )
                    )}
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        )}
      </div>

      {/* Universal picker modal for bulk-friendly edits */}
      {pickerOpen.open && (
        <UniversalPickerModal
          isOpen={pickerOpen.open}
          onClose={() =>
            !isUpdating &&
            setPickerOpen({
              open: false,
              mode: "helpdesk-label",
              title: "",
              targetIds: [],
              currentValue: null,
            })
          }
          mode={pickerOpen.mode}
          title={pickerOpen.title}
          config={{
            defaultValue: pickerOpen.currentValue,
            currentBuildingId: pickerOpen.currentBuildingId,
            // Contextwaarde naar de sleutel die deze picker effectief uitleest
            // (bv. de onderdeel-picker filtert op renovatieproject, niet op gebouw).
            ...(pickerOpen.contextConfigKey &&
            pickerOpen.contextConfigKey !== "currentBuildingId"
              ? { [pickerOpen.contextConfigKey]: pickerOpen.contextValue }
              : {}),
            customOptions: pickerOpen.customOptions,
            multiSelect: pickerOpen.multiSelect,
            ticketIds: pickerOpen.targetIds,
            ticketLabels: pickerOpen.targetIds
              .map((id) => rows.find((row) => row.id === id)?.label)
              .filter((label) => label !== TicketLabel.UNKNOWN) as string[],
            showFrequentSuppliers: pickerOpen.mode === "supplier",
            allowRemoval:
              pickerOpen.mode === "supplier" ||
              pickerOpen.mode === "reporter" ||
              pickerOpen.mode === "assignee",
            allowCreateSupplier: pickerOpen.mode === "supplier",
          }}
          onConfirm={async (val) => {
            setIsUpdating(true);
            try {
              // ========== NEW: Config-driven field mapping ==========
              // Find the field mapping configuration for this picker mode
              // Prefer eventMode (unique per field) over pickerMode (can be shared)
              const fieldMapping = pickerConfig?.fieldMappings.find(
                (m) =>
                  pickerOpen.eventMode
                    ? m.eventMode === pickerOpen.eventMode
                    : m.pickerMode === pickerOpen.mode,
              );

              if (!fieldMapping) {
                // FALLBACK: Legacy hardcoded logic (backward compatibility)
                console.warn(
                  `[UniversalTable] No picker config found for mode "${pickerOpen.mode}". Using legacy logic.`,
                );

                let updatePayload: any = {};
                let fieldName = "";

                if (pickerOpen.mode === "helpdesk-label") {
                  updatePayload = { label: val };
                  fieldName = "label";
                } else if (pickerOpen.mode === "assignee") {
                  updatePayload = { assignedTo: val?.id || null };
                  fieldName = "assignedTo";
                } else if (pickerOpen.mode === "building") {
                  updatePayload = { building: val ? { id: val.id } : null };
                  fieldName = "building";
                } else if (pickerOpen.mode === "unit") {
                  updatePayload = { unit: val ? { id: val.id } : null };
                  fieldName = "unit";
                } else if (pickerOpen.mode === "supplier") {
                  fieldName = "supplier";
                  // Multi-leverancier: voeg de gekozen leverancier TOE via het
                  // dedicated endpoint (een ticket kan meerdere leveranciers hebben).
                  if (val?.id) {
                    for (const id of pickerOpen.targetIds) {
                      try {
                        const res = await fetchWithAuth(
                          `${API_URL}/helpdesk/${id}/suppliers`,
                          {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ supplierId: val.id }),
                          },
                          session,
                        );
                        const updatedAssignments = await res
                          .json()
                          .catch(() => null);
                        if (Array.isArray(updatedAssignments)) {
                          onUpdate?.(id, {
                            supplierAssignments: updatedAssignments,
                          });
                        }
                      } catch (error) {
                        console.error(
                          `Failed to add supplier for ticket ${id}:`,
                          error,
                        );
                      }
                    }
                  }
                } else if (pickerOpen.mode === "reporter") {
                  // Include full reporter object with firstName and lastName for instant UI update
                  updatePayload = {
                    reporter: val
                      ? {
                          id: val.id,
                          firstName: val.firstName,
                          lastName: val.lastName,
                        }
                      : null,
                  };
                  fieldName = "reporter";
                } else if (pickerOpen.mode === "source") {
                  updatePayload = { source: val };
                  fieldName = "source";
                }

                // For non-supplier updates, use the regular update endpoint
                if (pickerOpen.mode !== "supplier") {
                  const updatePromises = pickerOpen.targetIds.map(
                    async (id) => {
                      try {
                        const response = await fetchWithAuth(
                          `${API_URL}/helpdesk/ticket/${id}`,
                          {
                            method: "PUT",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify(updatePayload),
                          },
                          session,
                        );
                        if (!response.ok) {
                          throw new Error(`Failed to update item ${id}`);
                        }
                        onUpdate?.(id, updatePayload);
                      } catch (error) {
                        console.error(`Failed to update item ${id}:`, error);
                        toast.error(`Fout bij updaten van item H${id}`);
                      }
                    },
                  );
                  await Promise.all(updatePromises);
                }

                const fieldNames: Record<string, string> = {
                  label: "Label",
                  assignee: "Toegewezen aan",
                  building: "Gebouw",
                  unit: "Unit",
                  supplier: "Leverancier",
                  reporter: "Melder",
                  source: "Bron",
                };
                toast.success(
                  `${fieldNames[pickerOpen.mode]} bijgewerkt voor ${pickerOpen.targetIds.length} ticket(s)`,
                );

                if (fieldName) {
                  await executeAfterUpdateActions(
                    fieldName,
                    val,
                    pickerOpen.targetIds,
                  );
                }
              } else {
                // ========== NEW: Config-driven update logic ==========
                const fieldName = fieldMapping.fieldName;

                // Check if custom update handler is provided
                if (fieldMapping.customUpdateHandler) {
                  // Use custom handler (e.g., for supplier with special endpoint)
                  await fieldMapping.customUpdateHandler(
                    val,
                    pickerOpen.targetIds,
                    session,
                    (rowId, payload) => onUpdate?.(rowId, payload),
                  );
                } else {
                  // Use default update logic with configured endpoint
                  const updatePayload = fieldMapping.buildUpdatePayload
                    ? fieldMapping.buildUpdatePayload(val)
                    : { [fieldName]: val };

                  const endpoint = pickerConfig?.apiUpdateEndpoint;
                  if (!endpoint) {
                    console.error(
                      "[UniversalTable] No apiUpdateEndpoint configured",
                    );
                    toast.error(
                      "Configuration error: No update endpoint defined",
                    );
                    return;
                  }

                  // Update each row in the database
                  const updatePromises = pickerOpen.targetIds.map(
                    async (id) => {
                      try {
                        const url = endpoint.replace("{id}", String(id));
                        const response = await fetchWithAuth(
                          `${API_URL}${url}`,
                          {
                            method: "PUT",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify(updatePayload),
                          },
                          session,
                        );
                        if (!response.ok) {
                          throw new Error(
                            `Failed to update ${pickerConfig?.entityName} ${id}`,
                          );
                        }
                        // Update local state
                        onUpdate?.(id, updatePayload);
                      } catch (error) {
                        console.error(
                          `Failed to update ${pickerConfig?.entityName} ${id}:`,
                          error,
                        );
                        const prefix = pickerConfig?.entityPrefix || "";
                        toast.error(
                          `Fout bij updaten van ${pickerConfig?.entityName} ${prefix}${id}`,
                        );
                      }
                    },
                  );

                  await Promise.all(updatePromises);
                }

                // Show success toast with configured labels
                const displayFieldName =
                  pickerConfig?.fieldLabels?.[fieldName] || fieldName;
                const entityCount = pickerOpen.targetIds.length;
                const entityName =
                  entityCount === 1
                    ? pickerConfig?.entityName
                    : pickerConfig?.entityPlural ||
                      `${pickerConfig?.entityName}s`;

                toast.success(
                  `${displayFieldName} bijgewerkt voor ${entityCount} ${entityName}`,
                );

                // Execute after-update column actions
                await executeAfterUpdateActions(
                  fieldName,
                  val,
                  pickerOpen.targetIds,
                );
              }

              // Always clear selected rows after applying changes
              setSelectedRows(new Set());
              // Close the modal after confirming
              setPickerOpen({
                open: false,
                mode: "helpdesk-label",
                title: "",
                targetIds: [],
                currentValue: null,
              });
            } catch (error) {
              console.error("Error updating rows:", error);
              toast.error("Fout bij het bijwerken");
            } finally {
              setIsUpdating(false);
            }
          }}
        />
      )}

      {/* Lazy Load Trigger for infinite scroll */}
      {lazyLoadTrigger}
    </section>
  );
};
